import './OrderSearchCriteriaPanel.css'
import {OrderSearchCriteria} from "./OrderSearchCriteria";
import React from "react";
import {Button, Collapse, FormGroup, Input, Label} from "reactstrap";
import {MultiSelect} from "../../input/MultiSelect";
import {Utils} from "../../../utils/Utils";
import {useGetOrderStatusesQuery, useGetSitesQuery} from "../../../app/apiSlice";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {
    isFetchingOrders,
    selectOrderSearchCriteria,
    selectOrderStatuses,
    setOrderSearchCriteria, STORAGE_KEY_ORDER_SEARCH_CRITERIA
} from "../../order/OrderSlice";
import {usePersistedSelector} from "../../../persistence";

interface OrderSearchCriteriaPanelProps {
    isOpen: boolean;
    readonly onSearchClick: () => void;
}

export const OrderSearchCriteriaPanel = ({isOpen, onSearchClick}: OrderSearchCriteriaPanelProps) => {
// Routing Hooks
    useGetOrderStatusesQuery();

// Hooks with no parameters
    const dispatch = useAppDispatch();

// Selectors
    const orderSearchCriteria = usePersistedSelector(
        STORAGE_KEY_ORDER_SEARCH_CRITERIA,
        selectOrderSearchCriteria);
    const statuses = useAppSelector(selectOrderStatuses);
    const isLoading = useAppSelector(isFetchingOrders);

    const setLocalCriteria = (value: ((v: unknown) => OrderSearchCriteria) | OrderSearchCriteria) => {
        if (typeof value === "function") {
            value = value(orderSearchCriteria);
        }
        return dispatch(setOrderSearchCriteria(value));
    };
    
// Other Hooks
    const {data: sites} = useGetSitesQuery();

// Helper Functions
    const changeHandle = Utils.handleChanges(setLocalCriteria);

// Other Variables
    function onResetClicked() {
        const defaultCriteria: OrderSearchCriteria = {
            statusIds: statuses?.map(s => s.id)
        };
        setLocalCriteria(defaultCriteria);
        dispatch(setOrderSearchCriteria(defaultCriteria));
    }

    const onEnterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !isLoading) {
            executeSearch()
        }
    };

    const executeSearch = () => {
        // dispatch(setOrderSearchCriteria(localCriteria));
        onSearchClick()
    };

    return (
        <Collapse horizontal isOpen={isOpen}>
            <div id='OrderSearchCriteriaContainer'>
                <ActionRow isLoading={isLoading}
                           onSearchClick={executeSearch} 
                           onResetClick={onResetClicked}
                           className={'mb-3'}
                />
                <FormGroup>
                    <Input placeholder='Order #'
                           id='OrderId'
                           type='number'
                           name='orderId'
                           autoComplete="autoComplete_off"
                           value={orderSearchCriteria.orderId?.toString() ?? ''}
                           onKeyDown={onEnterKeyDown}
                           onChange={changeHandle}/>
                    {(orderSearchCriteria.orderId ?? 1) <= 0 &&
                        <p className='text-danger'>Invalid Order #. It must be 1 or greater.</p>}
                </FormGroup>

                <FormGroup>
                    <Input id="StorefrontId" 
                           name="siteId"
                           autoComplete="autoComplete_off" 
                           type="select"
                           value={orderSearchCriteria.siteId ?? ''} onChange={changeHandle}>
                        <option value=''>Any Storefront</option>
                        {sites?.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </Input>
                </FormGroup>

                <FormGroup>
                    <Label for='StartDate'>Order Date</Label>
                    <Input id='StartDate' 
                           autoComplete="autoComplete_off" 
                           type='date'
                           max={orderSearchCriteria.endDate}
                           name='startDate'
                           value={orderSearchCriteria.startDate ?? ''}
                           onKeyDown={onEnterKeyDown}
                           onChange={changeHandle}/>
                    <Label for='EndDate'>to</Label>
                    <Input id='EndDate'
                           autoComplete="autoComplete_off"
                           type='date'
                           min={orderSearchCriteria.startDate}
                           name='endDate'
                           value={orderSearchCriteria.endDate ?? ''}
                           onKeyDown={onEnterKeyDown}
                           onChange={changeHandle}/>
                </FormGroup>

                <FormGroup className="criteria-panel-control-container">
                    <Label>Order Status</Label>
                    <MultiSelect options={statuses?.map(s => ({value: s.id, display: s.name})) ?? []}
                                 values={orderSearchCriteria.statusIds ?? []}
                                 onChange={ids => setLocalCriteria({...orderSearchCriteria, statusIds: ids})}/>
                    {orderSearchCriteria.statusIds && orderSearchCriteria.statusIds?.length < 1 &&
                        <p className='text-danger'>At least one Order Status must be selected.</p>}
                </FormGroup>

                <FormGroup>
                    <Input placeholder='Customer Id'
                           name='consumerId'
                           autoComplete="autoComplete_off"
                           value={orderSearchCriteria.consumerId ?? ''}
                           onKeyDown={onEnterKeyDown}
                           onChange={changeHandle}
                    />
                </FormGroup>
                <FormGroup>
                    <Input placeholder='Customer Name'
                           name='userName'
                           autoComplete="autoComplete_off"
                           value={orderSearchCriteria.userName ?? ''}
                           onKeyDown={onEnterKeyDown}
                           onChange={changeHandle}
                    />
                </FormGroup>
                <FormGroup>
                    <Input placeholder='ABA'
                           name='aba'
                           autoComplete="autoComplete_off"
                           value={orderSearchCriteria.aba ?? ''}
                           onKeyDown={onEnterKeyDown}
                           onChange={changeHandle}
                    />
                </FormGroup>
                <FormGroup>
                    <Input placeholder='Account Number'
                           name='accountNumber'
                           autoComplete="autoComplete_off"
                           value={orderSearchCriteria.accountNumber ?? ''}
                           onKeyDown={onEnterKeyDown}
                           onChange={changeHandle}
                    />
                </FormGroup>
                <FormGroup>
                    <Input placeholder='Email'
                           name='userEmail'
                           value={orderSearchCriteria.userEmail ?? ''}
                           onKeyDown={onEnterKeyDown}
                           onChange={changeHandle}
                    />
                </FormGroup>

                <ActionRow isLoading={isLoading}
                           onSearchClick={executeSearch}
                           onResetClick={onResetClicked} 
                           className={'mt-3'} 
                />
            </div>
        </Collapse>
    );
};

interface ActionRowProps {
    readonly className?: string;
    readonly isLoading: boolean;
    readonly onSearchClick: () => void;
    readonly onResetClick: () => void;
    
}
const ActionRow = ({className, onResetClick, onSearchClick, isLoading}: ActionRowProps) => (
    <div className={'d-flex justify-content-between flex-row-reverse ' + (className ?? '')}>
        <Button color='primary'
                onClick={onSearchClick}
                disabled={isLoading}
        >
            Search
        </Button>
        <Button onClick={onResetClick} disabled={isLoading}>
            Reset
        </Button>
    </div>
);